<template>
  <v-container>
    <v-row justify-center>
      <v-col>
        <v-divider />

        <!-- Titre personnalisé -->
        <v-row align="center" justify="center">
          <v-col cols="12" lg="3" xl="2">
            <h4 class="part-title">Titre personnalisé :</h4>
          </v-col>
          <v-col cols="12" lg="5" xl="3">
            <v-text-field
              v-model="currentDiscipline.skillCustomTitle"
              @change="updateDisciplineCustomLabel(currentDiscipline.skillCustomTitle)"
              placeholder="Compétences"
              outlined
              dense
              class="mr-5"
              :rules="[rules.counter]"
              counter
              maxlength="30"
              clearable
              :disabled="!canManageCurrentDiscipline"
            />
          </v-col>
          <v-col cols="12" lg="4" xl="7">
            <span style="font-style: italic">Le titre personnalisé sera affiché à la place de "Compétences".</span>
          </v-col>
        </v-row>

        <v-divider v-if="canManageCurrentDiscipline" />

        <!-- Add item -->
        <v-row v-if="canManageCurrentDiscipline">
          <v-col cols="12" lg="3" xl="2">
            <h4 class="part-title">Ajouter un élément de premier niveau :</h4>
          </v-col>
          <v-col cols="12" lg="5" xl="3">
            <v-text-field
              v-model="newSkill.title"
              outlined
              dense
              class="mr-5"
              :rules="[rules.required, rules.counter]"
              counter
              maxlength="30"
              hide-details="auto"
              clearable
            />
          </v-col>
          <v-col cols="12" lg="4" xl="7">
            <v-btn v-on:click="addSkill(newSkill, null)" color="primary" dark class="mb-2" elevation="2" :disabled="!newSkill.title"> Ajouter </v-btn>
          </v-col>
        </v-row>

        <v-divider />

        <!-- List of items -->
        <v-row v-if="skills && skills.items && skills.items.length > 0">
          <v-col cols="12">
            <h4 class="part-title">{{ getCutomTitle() }}</h4>
          </v-col>
          <v-col>
            <v-row>
              <v-col>
                <v-tooltip right>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn @click="toggleTreeview" v-bind="attrs" v-on="on" color="secondary">
                      <v-icon>{{ expanded ? "mdi-arrow-collapse-vertical" : "mdi-arrow-expand-vertical" }}</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ expanded ? "Tout réduire" : "Tout développer" }}</span>
                </v-tooltip>
              </v-col>
            </v-row>
            <v-row>
              <v-col cols="4">
                <v-treeview :items="skills.items" :open-all="expanded" ref="myTreeview">
                  <template slot="label" slot-scope="{ item }">
                    <v-text-field
                      @change="updateSkill(item)"
                      v-model="item.title"
                      outlined
                      dense
                      class="mr-5"
                      :rules="[rules.required, rules.counter]"
                      counter
                      maxlength="100"
                      hide-details="auto"
                      :disabled="!canManageCurrentDiscipline"
                    />
                  </template>

                  <template slot="append" slot-scope="{ item }">
                    <md-menu md-direction="bottom-start" style="padding-bottom: 25px">
                      <md-button class="md-icon-button p-2" md-menu-trigger v-if="canManageCurrentDiscipline">
                        <md-icon>add_circle_outline</md-icon>
                      </md-button>

                      <md-menu-content>
                        <md-menu-item v-on:click="addSkill(null, item)"> Ajouter un sous-élément </md-menu-item>

                        <md-menu-item v-on:click="deleteSkill(item.id)"> Supprimer un élément </md-menu-item>
                      </md-menu-content>
                    </md-menu>
                  </template>
                </v-treeview>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import store from "@/store";
import PermissionsService from "@/services/PermissionsService";

import { getSkillsForDisciplineAsync, createSkillForDisciplineAsync, updateSkillAsync, deleteSkillAsync } from "@/api/skillApi";
import { getDisciplineAsync, updateDisciplineAsync, getIsManagerPartOfDisciplineAsync } from "@/api/disciplinesApi";
import AuthService from "@/services/AuthService";
import { getIsUserPartOfFederationAsync } from "@/api/federationsApi";

export default {
  props: {
    disciplineId_prop: [Number, String],
  },

  data: () => ({
    isCurrentUserPartOfFederation: false,
    isCurrentUserPartOfDiscipline: false,
    authService: AuthService,
    federationId: null,
    disciplineId: null,
    skills: [],
    currentDiscipline: {},
    customTitle: "",
    expanded: true,
    newSkill: { title: "Nouvel élément" },
    rules: {
      required: (value) => !!value || "Requis",
      counter: (value) => (value ? value.length <= 100 : true),
    },
  }),

  watch: {
    $route() {
      this.initPage();
    },
  },

  computed: {
    canViewCurrentDiscipline() {
      // Can view own discipline and is part of current discipline
      let canViewOwnDisciplineAndIsPartOfCurrentDiscipline = PermissionsService.DisciplinesPermissionsService.canViewOwnDiscipline && this.isCurrentUserPartOfDiscipline;
      // Can view all Disciplines
      let canViewAllDisciplines = PermissionsService.DisciplinesPermissionsService.canViewAllDisciplinesOwnFederation;
      return canViewOwnDisciplineAndIsPartOfCurrentDiscipline || canViewAllDisciplines;
    },
    canManageCurrentDiscipline() {
      // Can manage own discipline and is part of current discipline
      let canManageCurrentDisciplineAndIsPartOfCurrentDiscipline =
        PermissionsService.DisciplinesPermissionsService.canManageOwnDiscipline && this.isCurrentUserPartOfDiscipline;
      // Can manage all disciplines of the current federation
      let canManageAllDisciplines = PermissionsService.DisciplinesPermissionsService.canManageAllDisciplinesOwnFederation && this.isCurrentUserPartOfFederation;
      return canManageCurrentDisciplineAndIsPartOfCurrentDiscipline || canManageAllDisciplines;
    },
  },

  async mounted() {
    await this.initPage();
  },

  methods: {
    async initPage() {
      // Override the props values from the url if provided
      this.federationId = this.$route.params.federationId ? parseInt(this.$route.params.federationId) : null;
      this.disciplineId = this.$route.params.disciplineId ? parseInt(this.$route.params.disciplineId) : this.disciplineId_prop;

      // Check if the current user is part of current federation
      this.isCurrentUserPartOfFederation = await getIsUserPartOfFederationAsync(this.authService.userId, this.federationId);
      // Check if the current user is part of current discipline
      this.isCurrentUserPartOfDiscipline = await getIsManagerPartOfDisciplineAsync(this.authService.userId, this.disciplineId);

      // Check if the user can see this page
      if (!this.canViewCurrentDiscipline) {
        store.dispatch("setSnackbar", {
          title: `Non Autorisé`,
          text: `Vous n'êtes pas autorisé à consulter cette ressource.`,
          status: "warning",
          timeout: 2000,
        });

        this.$router.replace("/");
      }

      this.getDisciplineForCustomLabel();
      this.getSkillsForDiscipline();
    },

    // get the custom title if exists
    getCutomTitle() {
      if (this.currentDiscipline && this.currentDiscipline.skillCustomTitle) {
        return this.currentDiscipline.skillCustomTitle;
      } else {
        return "Compétences";
      }
    },

    // Add a new Skill to the discipline
    async addSkill(current, parent) {
      var skillToCreate = {};
      skillToCreate.disciplineId = this.disciplineId;

      // If current, we wanna create a top level item
      if (current) {
        skillToCreate.title = current.title;
      }
      // Else we don't have a title yet but we have the parent item
      else {
        skillToCreate.hierarchyId = parent.hierarchyId;
      }

      var result = await createSkillForDisciplineAsync(this.disciplineId, skillToCreate);

      if (result) {
        store.dispatch("setSnackbar", {
          title: "Ajouté",
          text: "L'élément de la liste a bien été ajouté",
          status: "information",
          timeout: 1000,
        });
      }

      this.getSkillsForDiscipline();
    },

    // Delete an Skill
    async deleteSkill(id) {
      if (!id || !confirm("Êtes-vous sûr de vouloir supprimer cet élément et tous ses descendants ?")) return false;

      var result = await deleteSkillAsync(id);

      if (result) {
        store.dispatch("setSnackbar", {
          title: "Supprimé",
          text: "L'élément et tous ses descendants ont bien été supprimés",
          status: "information",
          timeout: 1000,
        });
      }

      this.getSkillsForDiscipline();
    },

    // Update an Skill
    async updateSkill(skill) {
      if (skill.title) {
        var skillCopy = { ...skill };
        // Empty the children collection
        skillCopy.children = [];

        var result = await updateSkillAsync(skillCopy);

        if (result) {
          store.dispatch("setSnackbar", {
            title: "Modifié",
            text: "L'élément de la liste a bien été modifié",
            status: "information",
            timeout: 1000,
          });
        }

        this.getSkillsForDiscipline();
      }
    },

    // Get the list of Skill
    async getSkillsForDiscipline() {
      this.skills = await getSkillsForDisciplineAsync(this.disciplineId);
    },

    // Get the current discipline to use the custom Skill title
    async getDisciplineForCustomLabel() {
      this.currentDiscipline = await getDisciplineAsync(this.disciplineId);

      if (!this.currentDiscipline) {
        store.dispatch("setSnackbar", {
          title: "Erreur",
          text: "Impossible de récupérer la discipline auprès du serveur",
          status: "error",
          timeout: 5000,
        });
      } else {
        this.customTitle = this.currentDiscipline.skillCustomTitle ? this.currentDiscipline.skillCustomTitle : this.customTitle;
      }
    },

    // Get the current discipline to use the custom Skill title
    async updateDisciplineCustomLabel(customTitle) {
      if (this.currentDiscipline) {
        this.currentDiscipline.skillCustomTitle = customTitle;

        var result = await updateDisciplineAsync(this.currentDiscipline.id, this.currentDiscipline);

        if (result) {
          store.dispatch("setSnackbar", {
            title: "Succès",
            text: "Le titre personnalisé a bien été mis à jour.",
            status: "success",
            timeout: 1000,
          });
        }

        this.getDisciplineForCustomLabel();
      } else {
        store.dispatch("setSnackbar", {
          title: "Erreur",
          text: "Impossible de récupérer la discipline auprès du serveur",
          status: "error",
          timeout: 5000,
        });
      }
    },

    toggleTreeview() {
      this.expanded = !this.expanded;
      this.$refs.myTreeview.updateAll(this.expanded);
    },
  },
};
</script>

<style scoped>
.part-title {
  font-family: Roboto, sans-serif;
  font-size: 16px;
  font-weight: 500;
}
.no-vert-padding {
  padding-top: 0;
  padding-bottom: 0;
}
/* >>> to select the current component and its childs only */
::v-deep .v-treeview-node__toggle {
  bottom: 12px;
}
</style>
